/**
 * Type compatibility layer
 * Re-exports Platform API and hand-written types with backward-compatible names
 */

import type { components } from './platform-api';

/**
 * `object`/`request_id` are wire-envelope bookkeeping (Stripe-style response
 * discriminator + request tracing) — no domain code reads them, and hand-built
 * synthetic values never set them, so every alias below omits them.
 */
type Resource<T> = Omit<T, 'object' | 'request_id'>;

export type IngestionConfiguration = Resource<components['schemas']['ConfigurationResponse']>;
export type UpdatedIngestionConfiguration = Resource<components['schemas']['UpdatedConfigurationResponse']>;
export type IngestionConfigurationVersion = Resource<components['schemas']['ConfigurationVersionResponse']>;
export type Messages = Resource<components['schemas']['MessageThreadResponse']>;
export type MessageSender = components['schemas']['MessageSender'];
export type Notes = Resource<components['schemas']['NoteResponse']>;
export type PaymentTenant = Resource<components['schemas']['PaymentTenantResponse']>;

/**
 * `client_id`/`operator_id`/`name` are optional on the API response (a generic
 * base-response concession) but `NOT NULL` on the underlying table — narrowed
 * back to reflect that real rows always have them.
 */
export type DebtType = Resource<components['schemas']['DebtTypeResponse']> & {
  client_id: string;
  operator_id: string;
  name: string;
};

/** `name`/`code` are `NOT NULL` on the underlying table; see DebtType above. */
export type Operator = Resource<components['schemas']['OperatorResponse']> & {
  name: string;
  code: string;
  active: boolean;
};

export type Templates = Resource<components['schemas']['TemplateResponse']>;

/**
 * Postgres enum public.domain_t — no Platform API endpoint returns this bare,
 * so it's hand-maintained here rather than derived.
 */
export type DomainT =
  | 'decisions'
  | 'communications'
  | 'governance'
  | 'journey'
  | 'commercials'
  | 'content'
  | 'theme'
  | 'financial'
  | 'payouts'
  | 'reporting'
  | 'agent'
  | 'people_costs'
  | 'connections'
  | 'ingestion_alert'
  | 'ingestion_sftp'
  | 'api_egress'
  | 'ingestion_api'
  | 'customer_display'
  | 'ingestion';

/** Postgres enum public.scope_t — same reasoning as DomainT. */
export type ScopeT = 'system' | 'operator' | 'debt_type' | 'client' | 'account' | 'case';

/**
 * Client — Platform API's ClientResponse only exposes id/display_name/status
 * today, far short of what the context-switcher construction sites need, so
 * this stays hand-written (same shape as the old Kanel row) until the API
 * response is widened.
 */
export interface Client {
  id: string;
  operator_id: string;
  display_name: string;
  legal_name: string;
  logo_url: string | null;
  metadata: unknown;
  ode_scope_key: string | null;
  registration_number: string | null;
  status: string | null;
  tax_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  workos_resource_id: string | null;
  branding: unknown;
}

export const Domain = {
  DECISIONS: 'decisions',
  COMMUNICATIONS: 'communications',
  GOVERNANCE: 'governance',
  JOURNEY: 'journey',
  COMMERCIALS: 'commercials',
  CONTENT: 'content',
  THEME: 'theme',
  FINANCIAL: 'financial',
  PAYOUTS: 'payouts',
  REPORTING: 'reporting',
  AGENT: 'agent',
  CONNECTIONS: 'connections',
  INGESTION_ALERT: 'ingestion_alert',
  INGESTION_SFTP: 'ingestion_sftp',
  API_EGRESS: 'api_egress',
  INGESTION_API: 'ingestion_api',
  CUSTOMER_DISPLAY: 'customer_display',
  INGESTION: 'ingestion',
} as const satisfies Record<string, DomainT>;

export const Scope = {
  SYSTEM: 'system',
  OPERATOR: 'operator',
  DEBT_TYPE: 'debt_type',
  CLIENT: 'client',
  ACCOUNT: 'account',
  CASE: 'case',
} as const satisfies Record<string, ScopeT>;

// =============================================================================
// Domain types - App
// =============================================================================
export type { HubApp, UserRole } from './domain/app';

// =============================================================================
// Domain types - Communication Design
// =============================================================================
export type {
  BlockId,
  BlockInstanceConfig,
  BlockType,
  BorderRadius,
  CommunicationDesign,
  CommunicationDesignConfig,
  CommunicationDesignConfigFormData,
  CommunicationDesignWithTypography,
  DesignBaseSettings,
  DesignChannel,
  DesignColors,
  DesignLayout,
  DesignSettings,
  DesignTemplateSource,
  DesignTypography,
  EmailBlock,
  LetterBlock,
  LetterLayoutConfig,
  Locale,
} from './domain/communication-design';

export {
  DEFAULT_COMMUNICATION_DESIGN,
  DEFAULT_DESIGN_BASE_SETTINGS,
  DEFAULT_TYPOGRAPHY,
  DESIGN_TEMPLATES,
  EMAIL_BLOCKS,
  FONT_OPTIONS,
  findBlockMetadata,
  getBlocksForChannel,
  MAX_CUSTOM_DESIGNS,
} from './domain/communication-design';
// =============================================================================
// Domain types - Email Components
// =============================================================================
export type {
  EmailBodyProps,
  EmailCTAProps,
  EmailFooterProps,
  EmailHeaderProps,
  EmailHeadingProps,
  EmailImageProps,
  EmailLayoutProps,
  EmailSeparatorProps,
} from './domain/email-components';

// =============================================================================
// Domain types - Reminders
// =============================================================================
// Reminders are now Template objects with is_reminder = true, loaded from database
// Use getReminderByType / getRemindersByChannel from @/services/templates
// =============================================================================
// Domain types - Templates
// =============================================================================
export type {
  EmailMetadata,
  GPTMetadata,
  LetterMetadata,
  LocalizationContent,
  NotificationKey,
  ReminderType,
  SMSMetadata,
  TagCategories,
  Template,
  TemplateChannel,
  TemplateContent,
  TemplateCreationRequest,
  TemplateDraft,
  TemplateFilters,
  TemplateFormality,
  TemplateMetadata,
  TemplateRow,
  TemplateScope,
  TemplateStatus,
  TemplateTone,
  TemplateType,
  WhatsAppApproval,
  WhatsAppMetadata,
} from './domain/template';
export {
  TEMPLATE_FORMALITIES,
  TEMPLATE_FORMALITY_LABELS,
  TEMPLATE_TONE_LABELS,
  TEMPLATE_TONES,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_TYPES,
} from './domain/template';
